/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { keyframes } from '@compiled/react';
import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import type { DocNode } from '@atlaskit/adf-schema';
import { cssMap, cx, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import {
	Breakpoint,
	useElementBreakpoints,
} from '@atlassian/generative-ai-modal/hooks/useElementBreakpoints';
import {
	RovoGradientKeyframesStyle,
	ROVO_GRADIENT_BACKGROUND,
} from '@atlassian/rovo-platform-ui-components/rovo-gradient';

import { useInlineChatController } from '../../controllers/inline-chat-controller';

import { AgentHeader } from './agent-header';
import messages from './messages';
import type { PromptContentChangePayload } from './prompt-content-change-payload';
import { ErrorView } from './views/error-view';
import { PromptView } from './views/prompt-view';
import { SuccessView } from './views/success-view';
import { ThinkingView } from './views/thinking-view';
import { WarningView } from './views/warning-view';

const slideFadeIn = keyframes({
	from: { opacity: 0, transform: 'translateY(1rem)' },
	to: { opacity: 1, transform: 'translateY(0)' },
});

const styles = cssMap({
	gradientWrapper: {
		animation: 'rovoGradientRotation linear 2s infinite',
		borderRadius: token('radius.xxlarge'),
		width: '50%',
		margin: '0 auto',
	},
	gradientInner: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.025'),
		backgroundColor: token('color.background.input'),
		borderRadius: 'inherit',
		backgroundClip: 'content-box',
	},
	wrapper: {
		width: 'fit-content',
		paddingInline: token('space.200'),
		paddingBlock: token('space.150'),
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.input'),
		boxShadow: token('elevation.shadow.overlay'),
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
	},

	fadeIn: {
		animationName: slideFadeIn,
		animationDuration: '0.2s',
		animationTimingFunction: 'ease-out',
		animationFillMode: 'forwards',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
			transition: 'none',
		},
	},

	wrapperFlex: {
		width: '100%',
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: token('space.100'),
	},

	agentHeaderSpacer: {
		// @ts-ignore
		marginTop: '46px', // eslint-disable-line @atlaskit/design-system/use-tokens-space
		paddingBottom: token('space.025'),
	},

	compact: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
	},

	compactStyle: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
		// @ts-ignore - calc with token not supported by cssMap types
		width: `calc(100% - ${token('space.300')})`, // eslint-disable-line @atlaskit/design-system/use-tokens-space
		margin: '0 auto',
	},

	fullWidth: {
		width: '100%',
	},

	thinking: {
		width: '50%',
		margin: '0 auto',
	},

	thinkingWithAnimatedBorder: {
		width: '100%',
		margin: '0 auto',
	},

	thinkingIsCancelling: {
		margin: '0 auto',
	},

	centering: {
		margin: '0 auto',
	},
});

type RovoInlineChatPromptProps = {
	source: string;
	productKey: string;
	siteId: string;
	orgId?: string;
	objectId?: string | number;
	onAbort: ({ streamEnded }: { streamEnded: boolean }) => void;
	onComplete: () => void;
	onSubmit?: (cancel: () => void) => void;
	onFollow?: () => void;
	compact?: boolean;
	onCancel?: () => void;
	autoClose?: boolean;
	enableAgents?: boolean;
	willDeleteAfterAbort?: boolean;
	onBeforeComplete?: (complete: () => void) => void;
	showMauiMediaPlaceholder?: boolean;
	/**
	 * When true, the side chat will open if Rovo encounters an error during the inline edit.
	 */
	openChatOnError?: boolean;
	/**
	 * Temporary prop to show beta lozenge - for use with editor inline only
	 * @private
	 * @deprecated
	 */
	_showLabsLozenge?: boolean;
	_showLabsLozengeVariant?: 'default' | 'maui';
	/**
	 * Used to control where popups are mounted to (ie. the editor typeahead)
	 */
	popupsMountPoint?: HTMLElement | null;
	/**
	 * Called when the inline prompt editor content changes (text length, empty state, mentions/cards).
	 */
	onPromptContentChange?: (payload: PromptContentChangePayload) => void;
	/**
	 * When provided, called with the prompt DocNode before the inline edit is sent to the
	 * Rovo bridge. If it returns `true`, the bridge send is skipped and the prompt is closed.
	 * Used by the SmartLink Rovo button to open the Rovo sidebar chat instead of an AI edit.
	 */
	onBeforeSubmit?: (prompt: DocNode) => boolean;
};

export const RovoInlineChatPrompt = (props: RovoInlineChatPromptProps): JSX.Element | null => {
	di(useInlineChatController);

	// When showMauiMediaPlaceholder is true, defer the completion transition so
	// the progress bar can fill to 100% before the thinking view unmounts.
	const [isProgressBarComplete, setIsProgressBarComplete] = useState(false);
	const pendingCompleteRef = useRef<(() => void) | null>(null);

	const { showMauiMediaPlaceholder, onBeforeComplete: propsOnBeforeComplete } = props;
	// Hidden when the media-node overlay is in use.
	const isProgressBarEnabled =
		!!showMauiMediaPlaceholder &&
		!(fg('cc-maui-phase-2-loading') && expValEquals('cc-maui-experiment', 'isEnabled', true)) &&
		!FeatureGates.getExperimentValue('maui_thinking_bar_progress_killswitch', 'value', false);

	const onBeforeComplete = useCallback(
		(complete: () => void) => {
			if (!isProgressBarEnabled) {
				if (propsOnBeforeComplete) {
					propsOnBeforeComplete(complete);
				} else {
					complete();
				}
				return;
			}
			// Defer until the progress bar's fill-to-100% animation finishes.
			const finalComplete = propsOnBeforeComplete
				? () => propsOnBeforeComplete(complete)
				: complete;
			pendingCompleteRef.current = finalComplete;
			setIsProgressBarComplete(true);
		},
		[isProgressBarEnabled, propsOnBeforeComplete],
	);

	const onCompleteAnimationFinished = useCallback(() => {
		pendingCompleteRef.current?.();
		pendingCompleteRef.current = null;
		setIsProgressBarComplete(false);
	}, []);

	const {
		mode,
		isCancelling,
		latestTrace,
		error,
		submit,
		resubmit,
		cancel,
		keep,
		abort,
		autoClose,
		agent,
		setAgent,
	} = useInlineChatController({ ...props, onBeforeComplete });
	const dialogRef = useRef<HTMLDivElement | null>(null);
	const [isBrowseAgentsModalOpen, setIsBrowseAgentsModalOpen] = useState(false);
	const [setBreakpointElement, { isSmall }] = useElementBreakpoints(Breakpoint.SmallChromeless);
	const { formatMessage } = useIntl();

	const useShortMessages =
		props.compact &&
		isSmall &&
		expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true);
	const shouldUseOldCompact =
		props.compact &&
		!expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true);
	const shouldUseNewCompact =
		props.compact &&
		expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true);

	const setDialogRef = useCallback(
		(element: HTMLDivElement | null) => {
			dialogRef.current = element;
			setBreakpointElement(element);
		},
		[setBreakpointElement],
	);

	useEffect(() => {
		const onMouseDown = (e: MouseEvent) => {
			// Don't auto-close during thinking mode — the user should use the stop
			// button or Escape key to cancel while a request is in progress.
			// Don't auto-close during error mode — clicking outside the error box
			// should not trigger the warning view.
			if (
				mode === 'thinking' ||
				(expValEquals('platform_editor_ai_fix_error_discard_screen', 'isEnabled', true) &&
					mode === 'error')
			) {
				return;
			}

			const isClickInsideDialog = dialogRef.current?.contains(e.target as Node);
			const isClickInsidePopupMount =
				fg('platform_editor_fix_inline_rendering') &&
				props.popupsMountPoint?.contains(e.target as Node);

			if (!isClickInsideDialog && !isClickInsidePopupMount && !isBrowseAgentsModalOpen) {
				if (autoClose) {
					cancel();
				}
			}
		};

		// eslint-disable-next-line @atlaskit/platform/no-direct-document-usage -- pre-existing, keyboard event listener
		return bind(document, {
			type: 'mousedown',
			listener: onMouseDown,
		});
	}, [mode, cancel, autoClose, isBrowseAgentsModalOpen, props.popupsMountPoint]);

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			cancel({ skipWarning: true });
		}
	};

	const willDeleteAfterAbort = props.willDeleteAfterAbort ?? true;
	const renderView = () => {
		if (isCancelling) {
			return (
				<WarningView
					onKeep={keep}
					onDelete={() => abort(true)}
					willDeleteAfterAbort={willDeleteAfterAbort}
				/>
			);
		}

		switch (mode) {
			case 'thinking':
				return (
					<ThinkingView
						latestTrace={latestTrace}
						onCancel={cancel}
						onFollow={props.onFollow}
						useShortMessages={useShortMessages}
						showProgressBar={isProgressBarEnabled}
						isComplete={isProgressBarComplete}
						onCompleteAnimationFinished={onCompleteAnimationFinished}
					/>
				);
			case 'editing':
				return (
					<PromptView
						{...props}
						showLabsLozenge={props._showLabsLozenge}
						labsLozengeVariant={props._showLabsLozengeVariant}
						showMauiMediaPlaceholder={props.showMauiMediaPlaceholder}
						onSubmit={submit}
						onAgentSelect={(a) => setAgent(a)}
						isBrowseAgentsModalOpen={isBrowseAgentsModalOpen}
						setIsBrowseAgentsModalOpen={setIsBrowseAgentsModalOpen}
						agent={agent}
						useShortMessages={useShortMessages}
					/>
				);
			case 'error':
				return <ErrorView error={error} onClose={abort} onTryAgain={resubmit} />;
			case 'success':
				return <SuccessView />;
			default:
				return null;
		}
	};

	const bar = (
		<Box
			ref={setDialogRef}
			role="dialog"
			aria-label={
				expValEquals('platform_editor_rovo_inline_chat_aria_label', 'isEnabled', true)
					? formatMessage(messages.dialogAriaLabel)
					: undefined
			}
			onKeyDown={onKeyDown}
			xcss={cx(
				styles.wrapper,
				// this is to the old compact style - to be removed after the new compact style is fully deployed
				shouldUseOldCompact && styles.compact,
				mode === 'editing' && styles.fullWidth,
				mode === 'thinking' &&
					!isCancelling &&
					(!isProgressBarEnabled && expValEquals('cc-maui-experiment', 'isEnabled', true)
						? styles.thinkingWithAnimatedBorder
						: styles.thinking),
				mode === 'thinking' && styles.thinkingIsCancelling,
				mode === 'thinking' && styles.fadeIn,
				shouldUseNewCompact && styles.compactStyle,
				expValEquals('platform_editor_ai_screen_layout_refactor', 'isEnabled', true) &&
					styles.centering,
			)}
		>
			{agent && (
				<AgentHeader agent={agent} removeAgent={() => setAgent(null)} compact={props.compact} />
			)}
			<Box xcss={cx(styles.wrapperFlex, agent && styles.agentHeaderSpacer)}>{renderView()}</Box>
		</Box>
	);

	if (
		mode === 'thinking' &&
		!isCancelling &&
		!isProgressBarEnabled &&
		expValEquals('cc-maui-experiment', 'isEnabled', true)
	) {
		return (
			<div
				css={styles.gradientWrapper}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- gradient background not expressible in cssMap
				style={{ background: ROVO_GRADIENT_BACKGROUND }}
				data-testid="thinking-bar-gradient-border"
			>
				<RovoGradientKeyframesStyle />
				<div css={styles.gradientInner}>{bar}</div>
			</div>
		);
	}

	return bar;
};
